import type {
  InstallationPreviewMechanism,
  InstallationPreviewRun,
  InstallationPreviewSnapshot,
  InstallationPreviewType,
  InstallationPreviewWorkspace,
} from 'types/installationPreview';
import { readInstallationStatus } from './installationVerification';

const invalid = () => new Error('Installation preview data could not be validated.');
const record = (value: unknown): value is Record<string, any> => !!value && typeof value === 'object' && !Array.isArray(value);
// Control characters are intentionally excluded from server-returned identifiers and reason codes.
// eslint-disable-next-line no-control-regex
const text = (value: unknown, max = 256): value is string => typeof value === 'string' && value.length > 0 && value.length <= max && !/[\u0000-\u001f]/.test(value);
const date = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value) && Number.isFinite(Date.parse(value));

export function installationPreviewDomain(value: unknown): string | null {
  return typeof value === 'string' && value.length < 254 && /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.myshopify\.com$/.test(value) ? value : null;
}

export function installationPreviewRequestKey(value: unknown): value is string {
  return typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(value);
}

export const installationPreviewPending = (run?: InstallationPreviewRun | null) => !run?.recoverable && (run?.status === 'queued' || run?.status === 'capturing');

export function installationPreviewType(mechanisms: InstallationPreviewMechanism[]): InstallationPreviewType {
  const custom = mechanisms.includes('manual_code');
  const self = mechanisms.includes('theme_embed') || mechanisms.includes('checkout_extension');
  return self && custom ? 'mixed' : self ? 'self' : custom ? 'custom' : 'unknown';
}

export function installationPreviewLabel(mechanisms: InstallationPreviewMechanism[]) {
  if (mechanisms.length > 1) return 'Multiple installation sources';
  return mechanisms[0] === 'theme_embed' ? 'Theme app embed'
    : mechanisms[0] === 'checkout_extension' ? 'Checkout extension'
      : mechanisms[0] === 'manual_code' ? 'Custom cart code' : 'Installation method unverified';
}

function jpegBytes(value: unknown): number {
  if (typeof value !== 'string' || value.length > 409623 || !value.startsWith('data:image/jpeg;base64,')) throw invalid();
  const payload = value.slice(23);
  if (!payload.length || payload.length % 4 || !/^[A-Za-z0-9+/]+={0,2}$/.test(payload)) throw invalid();
  let decoded: string;
  try { decoded = atob(payload); } catch { throw invalid(); }
  if (decoded.length > 300 * 1024 || decoded.charCodeAt(0) !== 255 || decoded.charCodeAt(1) !== 216
    || decoded.charCodeAt(decoded.length - 2) !== 255 || decoded.charCodeAt(decoded.length - 1) !== 217) throw invalid();
  return decoded.length;
}

export function readInstallationPreviewWorkspace(value: unknown, storeId: string, domain: string, now = Date.now()): InstallationPreviewWorkspace {
  if (!installationPreviewDomain(domain) || !record(value) || value.schemaVersion !== 1 || value.storeId !== storeId
    || value.shopDomain !== domain || typeof value.available !== 'boolean' || !text(value.reason, 1000) || value.runtimeStatus !== 'unverified') throw invalid();
  let imageBytes = 0;
  const run = (candidate: unknown): InstallationPreviewRun | null => {
    if (candidate === null) return null;
    if (!record(candidate) || !text(candidate.id, 128) || candidate.storeId !== storeId || candidate.shopDomain !== domain
      || !installationPreviewRequestKey(candidate.requestKey) || !['queued', 'capturing', 'completed', 'failed'].includes(candidate.status)
      || !Number.isSafeInteger(candidate.revision) || candidate.revision < 1 || !(candidate.themeId === null || text(candidate.themeId, 128))
      || !text(candidate.configurationFingerprint, 256) || !date(candidate.createdAt) || !date(candidate.updatedAt) || !date(candidate.expiresAt)
      || Date.parse(candidate.updatedAt) < Date.parse(candidate.createdAt) || Date.parse(candidate.expiresAt) < Date.parse(candidate.createdAt)
      || typeof candidate.stale !== 'boolean' || (candidate.recoverable !== undefined && typeof candidate.recoverable !== 'boolean')
      || !(candidate.reason === null || text(candidate.reason, 1000))) throw invalid();
    let snapshot: InstallationPreviewSnapshot | null = null;
    if (candidate.snapshot !== null) {
      const capture = candidate.snapshot;
      if (candidate.status !== 'completed' || !record(capture) || capture.schemaVersion !== 1 || capture.storeId !== storeId || capture.shopDomain !== domain
        || capture.themeId !== candidate.themeId || capture.configurationFingerprint !== candidate.configurationFingerprint
        || !date(capture.capturedAt) || Date.parse(capture.capturedAt) < Date.parse(candidate.createdAt) - 60000 || Date.parse(capture.capturedAt) > Date.parse(candidate.updatedAt) + 60000
        || capture.runtimeStatus !== 'unverified' || !Array.isArray(capture.mechanisms) || capture.mechanisms.length > 3
        || capture.mechanisms.some((mechanism: unknown) => !['theme_embed', 'checkout_extension', 'manual_code'].includes(mechanism as string))
        || new Set(capture.mechanisms).size !== capture.mechanisms.length || capture.installationType !== installationPreviewType(capture.mechanisms)
        || !Array.isArray(capture.surfaces) || !capture.surfaces.length || capture.surfaces.length > 6) throw invalid();
      const seen = new Set<string>();
      const surfaces = capture.surfaces.map((surface: unknown) => {
        if (!record(surface) || !['cart_page', 'cart_drawer', 'checkout'].includes(surface.surface) || !['desktop', 'mobile'].includes(surface.device)
          || !['captured', 'unavailable'].includes(surface.status) || !text(surface.reason, 1000)
          || !(surface.widgetCount === null || (Number.isSafeInteger(surface.widgetCount) && surface.widgetCount >= 0 && surface.widgetCount <= 100))) throw invalid();
        const id = `${surface.surface}:${surface.device}`;
        if (seen.has(id)) throw invalid();
        seen.add(id);
        if (surface.status === 'captured') {
          if (surface.surface === 'checkout' || surface.widgetCount === null || surface.widgetCount < 1) throw invalid();
          imageBytes += jpegBytes(surface.imageDataUrl);
          if (imageBytes > 1500 * 1024) throw invalid();
        } else if (surface.imageDataUrl !== null) throw invalid();
        return { surface: surface.surface, device: surface.device, status: surface.status, reason: surface.reason, imageDataUrl: surface.imageDataUrl, widgetCount: surface.widgetCount };
      });
      snapshot = { schemaVersion: 1, storeId, shopDomain: domain, themeId: candidate.themeId,
        configurationFingerprint: candidate.configurationFingerprint, capturedAt: capture.capturedAt, installationType: capture.installationType,
        mechanisms: [...capture.mechanisms], runtimeStatus: 'unverified', surfaces };
    } else if (candidate.status === 'completed' && !(candidate.stale === true && candidate.reason === 'preview_expired')) throw invalid();
    return { id: candidate.id, storeId, shopDomain: domain, requestKey: candidate.requestKey, status: candidate.status,
      revision: candidate.revision, themeId: candidate.themeId, configurationFingerprint: candidate.configurationFingerprint,
      createdAt: candidate.createdAt, updatedAt: candidate.updatedAt, expiresAt: candidate.expiresAt,
      stale: candidate.stale || Date.parse(candidate.expiresAt) <= now, recoverable: candidate.recoverable === true, reason: candidate.reason, snapshot };
  };
  const current = run(value.current);
  const previous = run(value.previous);
  if (previous && (previous.status !== 'completed' || previous.id === current?.id || (current && Date.parse(previous.createdAt) > Date.parse(current.createdAt)))) throw invalid();
  const installationStatus = value.installationStatus === undefined ? undefined
    : readInstallationStatus(value.installationStatus, storeId, domain, now);
  const currentBinding = (capture: InstallationPreviewRun | null) => capture && installationStatus
    && (capture.themeId !== installationStatus.themeId || capture.configurationFingerprint !== installationStatus.configurationFingerprint)
    ? { ...capture, stale: true } : capture;
  return { schemaVersion: 1, storeId, shopDomain: domain, available: value.available, reason: value.reason, current: currentBinding(current), previous: currentBinding(previous),
    runtimeStatus: 'unverified', ...(installationStatus ? { installationStatus } : {}) };
}
